import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useData } from '../context/DataContext';
import { Card, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Table } from '../components/ui/Table';
import { Modal } from '../components/ui/Modal';
import { EditCustomerModal } from '../components/domain/EditCustomerModal';
import { callBillingAPI } from '../utils/api';
import { formatCurrency } from '../utils/formatters';
import { ArrowLeftIcon, EditIcon, MailIcon, PhoneIcon, MapPinIcon, BuildingIcon, GlobeIcon, FileTextIcon, AlertCircleIcon, CheckCircleIcon, ClockIcon, XCircleIcon, CreditCardIcon, CalendarIcon, RepeatIcon, ChevronDownIcon, TrashIcon } from 'lucide-react';
// Types
interface CustomerDetails {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  organization: string;
  address_street: string;
  address_city: string;
  address_state: string;
  address_zip: string;
  terms_accepted: number | string | boolean;
  invoices_number?: number | string;
  subscriptions_number?: number | string;
  websites?: string;
}
interface CustomerInvoice {
  id: string;
  number: string;
  date: number;
  amount: number;
  status: string;
  source?: string;
}
interface CustomerSubscription {
  id: string;
  number: string;
  name: string;
  status: string;
  cycle: string;
  cycle_num: number;
  amount: number;
  next_invoice_date: number;
}
export function CustomerView() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const {
    selectedCompanyId,
    selectedCompany
  } = useData();
  // Check if current company is TKDscore
  const isTKDscore = selectedCompany?.name?.toLowerCase().includes('tkdscore') || false;
  const [customer, setCustomer] = useState<CustomerDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Invoices state
  const [invoices, setInvoices] = useState<CustomerInvoice[]>([]);
  const [invoicesPage, setInvoicesPage] = useState(1);
  const [invoicesHasMore, setInvoicesHasMore] = useState(false);
  const [isLoadingInvoices, setIsLoadingInvoices] = useState(false);
  const [totalInvoices, setTotalInvoices] = useState(0);
  // Subscriptions state
  const [subscriptions, setSubscriptions] = useState<CustomerSubscription[]>([]);
  const [subscriptionsPage, setSubscriptionsPage] = useState(1);
  const [subscriptionsHasMore, setSubscriptionsHasMore] = useState(false);
  const [isLoadingSubscriptions, setIsLoadingSubscriptions] = useState(false);
  const [totalSubscriptions, setTotalSubscriptions] = useState(0);
  // Edit Modal State
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  // Delete state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const hasInvoicesOrSubscriptions = totalInvoices > 0 || totalSubscriptions > 0;
  const convertTermsAccepted = (value: number | string | boolean): boolean => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value === 1;
    if (typeof value === 'string') return value === '1' || value === 'true';
    return false;
  };
  const fetchInvoices = async (page: number, append: boolean = false) => {
    if (!id || !selectedCompanyId) return;
    setIsLoadingInvoices(true);
    try {
      const response = await callBillingAPI('getInvoices', {
        company_id: selectedCompanyId,
        customer_id: id,
        page,
        per_page_limit: 5
      });
      const mapped = (response.items || []).map((inv: any) => ({
        id: inv.id,
        number: inv.number,
        date: inv.invoice_date || inv.created,
        amount: parseFloat(inv.amount) || 0,
        status: inv.status,
        source: inv.source || ''
      }));
      setInvoices(prev => append ? [...prev, ...mapped] : mapped);
      setInvoicesHasMore(response.has_next_page || false);
      setInvoicesPage(page);
    } catch (err) {
      console.error('Failed to load invoices:', err);
    } finally {
      setIsLoadingInvoices(false);
    }
  };
  const fetchSubscriptions = async (page: number, append: boolean = false) => {
    if (!id || !selectedCompanyId) return;
    setIsLoadingSubscriptions(true);
    try {
      const response = await callBillingAPI('getSubscriptions', {
        company_id: selectedCompanyId,
        customer_id: id,
        page,
        per_page_limit: 5
      });
      const mapped = (response.items || []).map((sub: any) => ({
        id: sub.id,
        number: sub.number,
        name: sub.name || '',
        status: sub.status,
        cycle: sub.cycle,
        cycle_num: parseInt(String(sub.cycle_num)) || 1,
        amount: parseFloat(String(sub.amount)) || 0,
        next_invoice_date: sub.next_invoice_date
      }));
      setSubscriptions(prev => append ? [...prev, ...mapped] : mapped);
      setSubscriptionsHasMore(response.has_next_page || false);
      setSubscriptionsPage(page);
    } catch (err) {
      console.error('Failed to load subscriptions:', err);
    } finally {
      setIsLoadingSubscriptions(false);
    }
  };
  const fetchData = async () => {
    if (!id) return;
    setIsLoading(true);
    setError(null);
    try {
      const customerData = await callBillingAPI('getCustomer', {
        id
      });
      setCustomer(customerData);
      const invCount = parseInt(String(customerData.invoices_number)) || 0;
      const subCount = parseInt(String(customerData.subscriptions_number)) || 0;
      setTotalInvoices(invCount);
      setTotalSubscriptions(subCount);
      // Only fetch if there are items
      if (invCount > 0) {
        fetchInvoices(1);
      } else {
        setInvoices([]);
        setInvoicesHasMore(false);
      }
      if (subCount > 0) {
        fetchSubscriptions(1);
      } else {
        setSubscriptions([]);
        setSubscriptionsHasMore(false);
      }
    } catch (err) {
      console.error('Failed to load customer data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load customer data');
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    fetchData();
  }, [id, selectedCompanyId]);
  const getStatusBadge = (status: string) => {
    const styles: Record<string, any> = {
      paid: {
        variant: 'success',
        label: 'Paid',
        icon: CheckCircleIcon
      },
      sent: {
        variant: 'info',
        label: 'Sent',
        icon: ClockIcon
      },
      overdue: {
        variant: 'danger',
        label: 'Overdue',
        icon: AlertCircleIcon
      },
      draft: {
        variant: 'default',
        label: 'Draft',
        icon: FileTextIcon
      },
      voided: {
        variant: 'default',
        label: 'Voided',
        icon: XCircleIcon
      },
      partial_paid: {
        variant: 'warning',
        label: 'Partial',
        icon: CreditCardIcon
      }
    };
    const config = styles[status] || styles.draft;
    const Icon = config.icon;
    return <Badge variant={config.variant} className="flex items-center gap-1" data-id="element-1023">
        <Icon className="w-3 h-3" data-id="element-1024" />
        {config.label}
      </Badge>;
  };
  const getSubStatusBadge = (status: string) => {
    const styles: Record<string, any> = {
      live: {
        variant: 'success',
        label: 'Live'
      },
      paused: {
        variant: 'warning',
        label: 'Paused'
      },
      cancelled: {
        variant: 'danger',
        label: 'Cancelled'
      },
      completed: {
        variant: 'default',
        label: 'Completed'
      }
    };
    const config = styles[status] || {
      variant: 'default',
      label: status
    };
    return <Badge variant={config.variant} data-id="element-1025">{config.label}</Badge>;
  };
  const getCycleName = (cycle: string, cycleNum: number = 1): string => {
    const names: Record<string, {
      singular: string;
      plural: string;
    }> = {
      week: {
        singular: 'Week',
        plural: 'Weeks'
      },
      month: {
        singular: 'Month',
        plural: 'Months'
      },
      year: {
        singular: 'Year',
        plural: 'Years'
      }
    };
    const n = names[cycle] || {
      singular: cycle,
      plural: cycle
    };
    return cycleNum === 1 ? `Every ${n.singular}` : `Every ${cycleNum} ${n.plural}`;
  };
  if (isLoading) {
    return <div className="flex items-center justify-center min-h-[400px]" data-id="element-1026">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" data-id="element-1027"></div>
      </div>;
  }
  if (error || !customer) {
    return <div className="p-8 text-center" data-id="element-1028">
        <div className="inline-flex items-center justify-center w-12 h-12 bg-red-100 rounded-full mb-4" data-id="element-1029">
          <AlertCircleIcon className="w-6 h-6 text-red-600" data-id="element-1030" />
        </div>
        <h2 className="text-lg font-semibold text-gray-900" data-id="element-1031">
          Customer Not Found
        </h2>
        <p className="text-gray-500 mt-2" data-id="element-1032">
          {error || 'The requested customer could not be found.'}
        </p>
        <Button className="mt-4" onClick={() => navigate('/customers')} data-id="element-1033">
          Back to Customers
        </Button>
      </div>;
  }
  const fullAddress = [customer.address_street, customer.address_city, customer.address_state, customer.address_zip].filter(Boolean).join(', ');
  return <div className="space-y-6" data-id="element-1034">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-1035">
        <div className="flex items-center gap-4" data-id="element-1036">
          <Button variant="ghost" size="sm" onClick={() => {
          const savedFilters = sessionStorage.getItem('customerFilters');
          if (savedFilters) {
            navigate(`/customers?${savedFilters}`);
          } else {
            navigate('/customers');
          }
        }} data-id="element-1037">
            <ArrowLeftIcon className="w-4 h-4 mr-2" data-id="element-1038" />
            Back
          </Button>
          <div data-id="element-1039">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-1040">
              {customer.first_name} {customer.last_name}
            </h1>
            {customer.organization && <div className="flex items-center gap-2 text-gray-500 mt-1" data-id="element-1041">
                <BuildingIcon className="w-4 h-4" data-id="element-1042" />
                <span data-id="element-1043">{customer.organization}</span>
              </div>}
          </div>
        </div>
        <div className="flex gap-2" data-id="element-1044">
          <Button onClick={() => setIsEditModalOpen(true)} leftIcon={<EditIcon className="w-4 h-4" data-id="element-1046" />} data-id="element-1045">
            Edit Customer
          </Button>
          <Button variant="danger" onClick={() => {
          setDeleteError(null);
          setShowDeleteModal(true);
        }} disabled={hasInvoicesOrSubscriptions} leftIcon={<TrashIcon className="w-4 h-4" data-id="element-1048" />} title={hasInvoicesOrSubscriptions ? 'Cannot delete a customer with invoices or subscriptions' : ''} data-id="element-1047">
            Delete
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-1049">
        {/* Left Column: Details */}
        <div className="space-y-6" data-id="element-1050">
          <Card data-id="element-1051">
            <CardHeader title="Contact Information" data-id="element-1052" />
            <div className="space-y-4" data-id="element-1053">
              <div className="flex items-start gap-3" data-id="element-1054">
                <MailIcon className="w-5 h-5 text-gray-400 mt-0.5" data-id="element-1055" />
                <div data-id="element-1056">
                  <p className="text-sm font-medium text-gray-500" data-id="element-1057">Email</p>
                  <a href={`mailto:${customer.email}`} className="text-blue-600 hover:underline" data-id="element-1058">
                    {customer.email}
                  </a>
                </div>
              </div>

              {customer.phone && <div className="flex items-start gap-3" data-id="element-1059">
                  <PhoneIcon className="w-5 h-5 text-gray-400 mt-0.5" data-id="element-1060" />
                  <div data-id="element-1061">
                    <p className="text-sm font-medium text-gray-500" data-id="element-1062">Phone</p>
                    <a href={`tel:${customer.phone}`} className="text-gray-900 hover:text-gray-700" data-id="element-1063">
                      {customer.phone}
                    </a>
                  </div>
                </div>}

              <div className="flex items-start gap-3" data-id="element-1064">
                <MapPinIcon className="w-5 h-5 text-gray-400 mt-0.5" data-id="element-1065" />
                <div data-id="element-1066">
                  <p className="text-sm font-medium text-gray-500" data-id="element-1067">Address</p>
                  <p className="text-gray-900" data-id="element-1068">
                    {fullAddress || <span className="text-gray-400 italic" data-id="element-1069">
                        No address provided
                      </span>}
                  </p>
                </div>
              </div>

              {(() => {
              const websitesList = (customer.websites || '').split('\n').map(w => w.trim()).filter(Boolean);
              if (websitesList.length === 0) return null;
              return <div className="flex items-start gap-3" data-id="element-1070">
                    <GlobeIcon className="w-5 h-5 text-gray-400 mt-0.5" data-id="element-1071" />
                    <div data-id="element-1072">
                      <p className="text-sm font-medium text-gray-500" data-id="element-1073">
                        Websites
                      </p>
                      <div className="space-y-1" data-id="element-1074">
                        {websitesList.map((site, i) => {
                      const href = site.startsWith('http') ? site : `https://${site}`;
                      return <a key={i} href={href} target="_blank" rel="noopener noreferrer" className="block text-blue-600 hover:underline text-sm" data-id="element-1075">
                              {site}
                            </a>;
                    })}
                      </div>
                    </div>
                  </div>;
            })()}

              <div className="pt-4 border-t border-gray-100" data-id="element-1076">
                <div className="flex items-center justify-between" data-id="element-1077">
                  <span className="text-sm text-gray-500" data-id="element-1078">Terms Accepted</span>
                  <Badge variant={convertTermsAccepted(customer.terms_accepted) ? 'success' : 'warning'} data-id="element-1079">
                    {convertTermsAccepted(customer.terms_accepted) ? 'Yes' : 'No'}
                  </Badge>
                </div>
              </div>

              {isTKDscore && <div className="pt-4 border-t border-gray-100" data-id="element-1080">
                  <div className="flex items-center justify-between" data-id="element-1081">
                    <span className="text-sm text-gray-500" data-id="element-1082">
                      Wholesale Customer
                    </span>
                    <Badge variant={(customer as any).wholesale_customer ? 'success' : 'default'} data-id="element-1083">
                      {(customer as any).wholesale_customer ? 'Yes' : 'No'}
                    </Badge>
                  </div>
                </div>}
            </div>
          </Card>
        </div>

        {/* Right Column: Invoices & Subscriptions */}
        <div className="lg:col-span-2 space-y-6" data-id="element-1084">
          {/* Invoices */}
          <Card data-id="element-1085">
            <CardHeader title={`Invoices${totalInvoices > 0 ? ` (${totalInvoices})` : ''}`} action={<Button size="sm" variant="secondary" onClick={() => navigate('/invoices/new', {
            state: {
              customerId: id,
              customerName: `${customer.first_name} ${customer.last_name}`.trim()
            }
          })} data-id="element-1087">
                  Create Invoice
                </Button>} data-id="element-1086" />
            {totalInvoices === 0 ? <div className="py-8 text-center text-gray-500 text-sm" data-id="element-1088">
                No invoices found for this customer
              </div> : <>
                <Table columns={[{
              key: 'number',
              header: 'Number',
              render: inv => <div data-id="element-1090">
                          <span className="font-medium text-blue-600 cursor-pointer hover:underline" onClick={() => navigate(`/invoices/${inv.id}`)} data-id="element-1091">
                            {inv.number}
                          </span>
                          {inv.source && <p className="text-xs text-gray-400 mt-0.5" data-id="element-1092">
                              {inv.source}
                            </p>}
                        </div>
            }, {
              key: 'date',
              header: 'Date',
              render: inv => <div className="flex items-center gap-2 text-gray-600" data-id="element-1093">
                          <CalendarIcon className="w-3 h-3" data-id="element-1094" />
                          {new Date(inv.date * 1000).toLocaleDateString()}
                        </div>
            }, {
              key: 'amount',
              header: 'Amount',
              render: inv => formatCurrency(inv.amount)
            }, {
              key: 'status',
              header: 'Status',
              render: inv => getStatusBadge(inv.status)
            }]} data={invoices} keyExtractor={inv => inv.id} emptyMessage="Loading..." data-id="element-1089" />
                {invoicesHasMore && <div className="p-4 border-t border-gray-200 text-center" data-id="element-1095">
                    <Button variant="ghost" size="sm" onClick={() => fetchInvoices(invoicesPage + 1, true)} isLoading={isLoadingInvoices} leftIcon={<ChevronDownIcon className="w-4 h-4" data-id="element-1097" />} data-id="element-1096">
                      Show More Invoices
                    </Button>
                  </div>}
              </>}
          </Card>

          {/* Subscriptions */}
          <Card data-id="element-1098">
            <CardHeader title={`Subscriptions${totalSubscriptions > 0 ? ` (${totalSubscriptions})` : ''}`} action={<Button size="sm" variant="secondary" onClick={() => navigate('/subscriptions/new', {
            state: {
              customerId: id,
              customerName: `${customer.first_name} ${customer.last_name}`.trim()
            }
          })} data-id="element-1100">
                  Create Subscription
                </Button>} data-id="element-1099" />
            {totalSubscriptions === 0 ? <div className="py-8 text-center text-gray-500 text-sm" data-id="element-1101">
                No subscriptions found for this customer
              </div> : <>
                <Table columns={[{
              key: 'number',
              header: 'Number',
              render: sub => <span className="font-medium text-blue-600 cursor-pointer hover:underline" onClick={() => navigate(`/subscriptions/${sub.id}`)} data-id="element-1103">
                          {sub.number}
                        </span>
            }, {
              key: 'name',
              header: 'Name',
              render: sub => <span className="text-gray-900" data-id="element-1104">{sub.name || '—'}</span>
            }, {
              key: 'cycle',
              header: 'Cycle',
              render: sub => <span className="text-gray-600 text-sm" data-id="element-1105">
                          {getCycleName(sub.cycle, sub.cycle_num)}
                        </span>
            }, {
              key: 'amount',
              header: 'Amount',
              render: sub => formatCurrency(sub.amount)
            }, {
              key: 'status',
              header: 'Status',
              render: sub => getSubStatusBadge(sub.status)
            }]} data={subscriptions} keyExtractor={sub => sub.id} emptyMessage="Loading..." data-id="element-1102" />
                {subscriptionsHasMore && <div className="p-4 border-t border-gray-200 text-center" data-id="element-1106">
                    <Button variant="ghost" size="sm" onClick={() => fetchSubscriptions(subscriptionsPage + 1, true)} isLoading={isLoadingSubscriptions} leftIcon={<ChevronDownIcon className="w-4 h-4" data-id="element-1108" />} data-id="element-1107">
                      Show More Subscriptions
                    </Button>
                  </div>}
              </>}
          </Card>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)} title="Delete Customer" size="sm" data-id="element-1109">
        <div className="space-y-4" data-id="element-1110">
          <p className="text-gray-600" data-id="element-1111">
            Are you sure you want to delete{' '}
            <span className="font-semibold text-gray-900" data-id="element-1112">
              {customer.first_name} {customer.last_name}
            </span>
            ? This action cannot be undone.
          </p>
          {deleteError && <div className="p-3 bg-red-50 border border-red-200 rounded-lg" data-id="element-1113">
              <p className="text-sm text-red-600" data-id="element-1114">{deleteError}</p>
            </div>}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200" data-id="element-1115">
            <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={isDeleting} data-id="element-1116">
              Cancel
            </Button>
            <Button variant="danger" onClick={async () => {
            setIsDeleting(true);
            setDeleteError(null);
            try {
              await callBillingAPI('deleteCustomer', {
                id: customer.id
              });
              setShowDeleteModal(false);
              const savedFilters = sessionStorage.getItem('customerFilters');
              navigate(savedFilters ? `/customers?${savedFilters}` : '/customers');
            } catch (err) {
              setDeleteError(err instanceof Error ? err.message : 'Failed to delete customer');
            } finally {
              setIsDeleting(false);
            }
          }} isLoading={isDeleting} leftIcon={<TrashIcon className="w-4 h-4" data-id="element-1118" />} data-id="element-1117">
              Delete
            </Button>
          </div>
        </div>
      </Modal>

      <EditCustomerModal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} onSaved={() => fetchData()} customerId={id} data-id="element-1119" />
    </div>;
}